import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import NotFound from "@/pages/NotFound";
import { ARTICLES } from "@/data/articles";
import { COMPANY } from "@/config/company";

export default function Article() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  if (!article) return <NotFound />;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: { "@type": "Organization", name: COMPANY.name },
    publisher: { "@type": "Organization", name: COMPANY.name },
  };

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        path={`/insights/${article.slug}`}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Insights", path: "/insights" },
          { name: article.title, path: `/insights/${article.slug}` },
        ]}
        schema={schema}
        ogImage={`/covers/${article.slug}.png`}
      />
      <article data-testid="article-page">
        <header className="relative bg-gradient-to-br from-navy via-navy-800 to-navy-900 pt-32 sm:pt-36 pb-14 sm:pb-16 overflow-hidden">
          <div className="absolute inset-0 grid-pattern-dark" aria-hidden="true" />
          <div className="container-site relative max-w-3xl">
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-slate-400">
                <li><Link to="/" data-testid="article-breadcrumb-home" className="hover:text-teal transition-colors">Home</Link></li>
                <li aria-hidden="true">/</li>
                <li><Link to="/insights" data-testid="article-breadcrumb-insights" className="hover:text-teal transition-colors">Insights</Link></li>
                <li aria-hidden="true">/</li>
                <li aria-current="page" className="text-teal">{article.category}</li>
              </ol>
            </nav>
            <Reveal>
              <p className="eyebrow-light mb-4">{article.category}</p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1] text-white">
                {article.title}
              </h1>
              <div className="flex items-center gap-5 text-sm text-slate-400">
                <span className="inline-flex items-center gap-1.5"><CalendarDays size={14} aria-hidden="true" />{article.date}</span>
                <span className="inline-flex items-center gap-1.5"><Clock size={14} aria-hidden="true" />{article.readTime}</span>
                <span>By {COMPANY.name}</span>
              </div>
            </Reveal>
          </div>
        </header>

        <div className="container-site max-w-4xl -mt-2 pt-8 bg-white">
          <Reveal>
            <img
              src={`/covers/${article.slug}.png`}
              alt={`Cover artwork for: ${article.title}`}
              className="w-full rounded-2xl border border-slate-200 shadow-md"
              data-testid="article-cover-image"
            />
          </Reveal>
        </div>

        <div className="bg-white py-14 sm:py-20">
          <div className="container-site max-w-3xl">
            <Reveal>
              <p className="text-lg sm:text-xl leading-relaxed text-ink font-medium border-l-4 border-teal pl-6">
                {article.intro}
              </p>
            </Reveal>
            <div className="mt-12 space-y-12">
              {article.sections.map((s, i) => (
                <Reveal key={i} delay={0.05}>
                  <section>
                    <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-navy">{s.heading}</h2>
                    {s.body.map((p, j) => (
                      <p key={j} className="mt-4 text-base leading-relaxed text-muted">{p}</p>
                    ))}
                  </section>
                </Reveal>
              ))}
            </div>
            <Reveal className="mt-14">
              <div className="rounded-2xl bg-mist border border-slate-200 p-8 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
                <div>
                  <h2 className="font-heading text-lg font-bold text-navy">Need support with this in your organization?</h2>
                  <p className="mt-1.5 text-sm text-muted">Our consultants work on exactly these challenges every day.</p>
                </div>
                <Link
                  to="/contact"
                  data-testid="article-cta-btn"
                  className="shrink-0 inline-flex items-center gap-2 bg-corp hover:bg-bright text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors"
                >
                  Talk to Our Team <ArrowRight size={15} aria-hidden="true" />
                </Link>
              </div>
              <Link
                to="/insights"
                data-testid="article-back-link"
                className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-corp hover:text-bright transition-colors"
              >
                <ArrowLeft size={15} aria-hidden="true" /> Back to all insights
              </Link>
            </Reveal>
          </div>
        </div>
      </article>
      <CTASection />
    </>
  );
}
